import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Link, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useDispatch } from 'react-redux';
import qs from 'query-string';

import { searchForArtistsAlgolia } from 'api/artist';
import { artistProfileApi, useCreateArtistProfileMutation } from 'api/artistProfile';
import ENTITY_PROPTYPES from 'config/constants/propTypes';
import routeMap from 'config/routes';
import { addQueryParams } from 'helpers/url';
import { generateSelectorsFromTestIds } from 'helpers/utilities';

import usePrevious from 'hooks/usePrevious';
import Card from 'components/Card';
import { TextEmphasis } from '@protonradio/proton-ui';
import TextHeader from 'components/TextHeader';
import WizardStep from 'components/Wizard/WizardStep';
import WizardSearchCard from 'components/Wizard/WizardSearchCard';
import ArtistSummaryRow from 'components/ArtistSummaryRow';
import { addAlertMessage, showAlert } from 'redux/actions/ui';

const CompoundErrorMessage = (
  <>
    Oops! Looks like your artist profile has multiple names in it. Please add each artist
    separately (
    <a href={routeMap.external.compoundArtists} target="_blank" rel="noreferrer">
      learn more here
    </a>
    ), or contact us for help:{' '}
    <a href="mailto:support@protonradio.com" target="_top">
      support@protonradio.com
    </a>
  </>
);

const ArtistName = ({ artistProfile, ...rest }) => {
  const { handlePrevious, handleContinue, ...wizardProps } = rest;

  const [createArtistProfile] = useCreateArtistProfileMutation();
  const { search } = useLocation();
  const { artistName: artistNameParam, selectNewArtist } = queryString.parse(search);

  // Artist fetching
  const [isFetchingArtists, setIsFetchingArtists] = useState(false);
  const [artistResults, setArtistResults] = useState([]);

  const artistName =
    artistProfile?.name || (artistNameParam && decodeURI(artistNameParam));
  const artistId = artistProfile?.id;

  // Input state
  const [searchInputText, setSearchInputText] = useState(artistName || '');
  const [searchQuery] = useDebounce(searchInputText, 500);
  const prevSearchQuery = usePrevious(searchQuery);

  // artist validation
  const [artistValidationError, setArtistValidationError] = useState(null);

  // selected artist
  const [selectedArtist, setSelectedArtist] = useState(() => {
    if (artistProfile) return artistProfile;
    if (selectNewArtist && artistNameParam) return 'newArtist';
    return null;
  });

  //UI state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedArtistId = selectedArtist?.id;
  const initialFetch = useRef(false);

  const dispatch = useDispatch();
  const onArtistLookupContinue = useCallback(() => {
    // if exisiting artist selected and artistId already exists in the url, then there's nothing to
    // do in this step.
    if (selectedArtistId && selectedArtistId === artistId) return Promise.resolve();

    // if selecting a new artist, fetch the artist and add the id to the url
    if (selectedArtistId) {
      const fetchArtistAction = artistProfileApi.endpoints.getArtistProfile.initiate;
      return dispatch(
        fetchArtistAction(selectedArtistId, {
          subscribe: false,
          forceRefetch: true
        })
      ).then(() => addQueryParams({ artistId: selectedArtistId }));
    }

    // If a new artist, we need to create the artist prior to navigating so we have an id to reference
    return (
      createArtistProfile(searchQuery)
        .unwrap()
        // NOTE: we add the newArtist flag in the url in case other parts of the wizard want to know if
        // this artist was created during this flow or not...
        .then(artist => {
          addQueryParams({ artistId: artist.id, newArtist: true });
        })
        .catch(e => {
          // Check for field specific errors related to validation. These will be displayed in the search input
          const fieldError = e.error?.name;

          // if non-specific field error, throw generic alert style error
          if (!fieldError || fieldError.length === 0) {
            dispatch(showAlert(e));
            throw e;
          }

          const compoundError = fieldError.find(error =>
            error.includes('cannot be compounded')
          );

          if (compoundError) {
            // TODO: ideally we wouldn't set react component into our store, but this would be a big refactor
            // to support via portals
            dispatch(addAlertMessage({ type: 'warning', message: CompoundErrorMessage }));
            setArtistValidationError(true);
            throw e;
          }

          const validationError = `Artist name ${fieldError[0]}`;
          setArtistValidationError(validationError);

          throw e; // rethrow the error so that we don't navigate ahead
        })
    );
  }, [dispatch, searchQuery, selectedArtistId, artistId, createArtistProfile]);

  useEffect(() => {
    // If artistProfile is present, then the user has an artist selected and we disable the input
    if (prevSearchQuery === searchQuery || artistProfile) return;

    if (!searchQuery) {
      setArtistResults([]);
      setIsFetchingArtists(false);
      return;
    }

    setIsFetchingArtists(true);

    searchForArtistsAlgolia({ query: searchQuery })
      .then(artists => {
        initialFetch.current = true;

        setArtistResults(artists);
        setIsFetchingArtists(false);
      })
      .catch(e => {
        dispatch(showAlert(e));
        setArtistResults([]);
        setIsFetchingArtists(false);
      });
  }, [searchQuery, prevSearchQuery, artistProfile, dispatch]);

  const title = artistProfile ? 'Artist Name' : 'What’s the artist name?';
  const subtitle = artistProfile ? (
    <>
      You are editing the profile for <TextEmphasis>{artistProfile.name}</TextEmphasis>.
      If you want to edit a different artist you can click{' '}
      <Link
        to={location => ({
          ...location,
          search: qs.stringify({
            ...qs.parse(location.search),
            // we want to unset the artist data in the url but keep any other query params
            artistId: undefined,
            artistName: undefined
          }),
          pathname: `/artist-profile-editor/${wizardProps.context}/overview`
        })}
        data-testid={ArtistName.TEST_IDS.START_OVER}
      >
        here
      </Link>{' '}
      to start over.
    </>
  ) : (
    `We'll search for existing profiles on Proton.`
  );

  return (
    <WizardStep
      {...wizardProps}
      title={title}
      subtitle={subtitle}
      onContinue={() => {
        setIsSubmitting(true);
        handleContinue(onArtistLookupContinue).finally(() => {
          setIsSubmitting(false);
        });
      }}
      onPrevious={handlePrevious}
      isContinueDisabled={
        (!selectedArtist && !artistProfile) || !!artistValidationError || isSubmitting
      }
      continueDisabledText={
        artistValidationError
          ? 'You must fix the error above before proceeding'
          : undefined
      }
    >
      <WizardStep.Section>
        {artistProfile ? (
          <Card fitHeight>
            <Card.Content>
              <ArtistSummaryRow artist={artistProfile} />
            </Card.Content>
          </Card>
        ) : (
          <WizardSearchCard
            data={artistResults}
            isFetching={isFetchingArtists}
            searchText={searchInputText}
            searchError={artistValidationError}
            onSearchTextChange={e => {
              const { value } = e.target;
              setSearchInputText(value);

              // We want to clear validation errors immediately
              if (artistValidationError) setArtistValidationError(null);

              // optimizations for how results are displayed as user types
              if (!value) {
                setArtistResults([]);
                setSelectedArtist(null);
              }
              if (value && !artistResults.length) setIsFetchingArtists(true);
            }}
            renderItemTombstone={index => (
              <WizardSearchCard.Item key={index}>
                <ArtistSummaryRow.Tombstone index={index} key={index} />
              </WizardSearchCard.Item>
            )}
            keyAccessor={data => data.id}
            renderItem={({ data }) => (
              <WizardSearchCard.Item
                key={data.id}
                active={selectedArtist?.id && selectedArtist?.id === data.id}
                onClick={() => {
                  setArtistValidationError(null);
                  setSelectedArtist(data);
                }}
                data-testid={ArtistName.TEST_IDS.ARTIST_SEARCH_RESULT}
              >
                <ArtistSummaryRow
                  artist={data}
                  lazyLoadImage={false}
                  data-testid={ArtistName.TEST_IDS.getTestIdForArtistSearchResult(
                    data.id
                  )}
                />
              </WizardSearchCard.Item>
            )}
            noSearchResultsTitle="No profiles on Proton found with that name!"
            noSearchResultsSubtitle="Click the link below to create a new one."
          />
        )}
      </WizardStep.Section>

      {/* NOTE: For now, we only show new artist options if user came to wizard without artistId param */}
      {!artistProfile && initialFetch.current && searchInputText && (
        <WizardStep.Section>
          <Card
            fitHeight
            onClick={() => {
              setArtistValidationError(null);
              setSelectedArtist(_artist =>
                _artist === 'newArtist' ? null : 'newArtist'
              );
            }}
            isActive={selectedArtist === 'newArtist'}
            data-testid={ArtistName.TEST_IDS.NEW_ARTIST_SELECT}
          >
            <Card.Content>
              <TextHeader type={TextHeader.TYPES.MEDIUM}>
                {artistResults.length > 0
                  ? 'None of these artists match?'
                  : "Can't find the artist?"}
              </TextHeader>
              Click here and we&apos;ll create a brand new artist profile for{' '}
              <TextEmphasis>{searchInputText}</TextEmphasis>
            </Card.Content>
          </Card>
        </WizardStep.Section>
      )}
    </WizardStep>
  );
};

ArtistName.propTypes = {
  artistProfile: ENTITY_PROPTYPES.ARTIST_PROFILE,
  onContinue: PropTypes.func
};

ArtistName.TEST_IDS = {
  ARTIST_SEARCH_RESULT: 'ArtistName-artist-result',
  START_OVER: 'ArtistName-start-over',
  NEW_ARTIST_SELECT: 'ArtistName-create-new-artist',
  getTestIdForArtistSearchResult: artistId => `ArtistName-artist-result-${artistId}`
};

ArtistName.SELECTORS = generateSelectorsFromTestIds(ArtistName.TEST_IDS);

export default ArtistName;
